import { getAuth0State } from './auth0State';
import { isAuth0Configured } from './config';

// Two markers, because ending the app session and ending the tenant's are different things.
//
// auth0Session: this browser's app session is (also) Auth0's, so restore it on load. Code that
// runs before the SDK has restored anything -- the boot token check, a forced logout -- cannot
// ask the SDK: a deployed build keeps its tokens in memory, which is empty at every page load.
// Set by the sign-in callback, and by Auth0SessionGate for an SDK session this tab has not
// marked yet. Cleared by every app sign-out and by a restore that fails.
//
// auth0TenantSession: a Universal Login finished here, so a tenant SSO session may be live. Set
// only by a sign-in callback that succeeds -- CallbackPage ends the one a failed callback
// leaves when no app session is marked, and a Custom Token Exchange creates none -- and
// cleared only right before the tenant's own logout. It outlives app sign-outs on purpose: a
// forced logout leaves the tenant session running, and without this the next sign-out would
// not know to end it, so the next /login would sign the member straight back in. And since only that logout clears it, a tab whose
// auth0Session marker another tab cleared can tell a sign-out from a restore that failed there.
const SESSION_KEY = 'auth0Session';
const TENANT_SESSION_KEY = 'auth0TenantSession';

function readSessionMarker(): boolean {
    return window.localStorage.getItem(SESSION_KEY) === 'true';
}

// The auth0Session marker as this tab last knew it: as found at page load, then as this tab last
// set or cleared it. Every tab shares the marker but not this, so where the two differ another
// tab has since signed in, signed out or failed to restore. Read at load, not later: a later
// read could already include another tab's change.
let sessionMarkedHere = readSessionMarker();

export function markAuth0Session(): void {
    window.localStorage.setItem(SESSION_KEY, 'true');
    sessionMarkedHere = true;
}

// Not without tenant values, even if a build that had them left the marker: a build without
// them must boot as it did before Auth0, and nothing in it can restore that session anyway.
export function hasAuth0Session(): boolean {
    return isAuth0Configured() && window.localStorage.getItem(SESSION_KEY) === 'true';
}

export function clearAuth0Session(): void {
    window.localStorage.removeItem(SESSION_KEY);
    sessionMarkedHere = false;
}

/**
 * Whether this tab found the auth0Session marker at page load or set it since, and has not
 * cleared it. Unlike hasAuth0Session(), nothing another tab does changes it.
 */
export function isAuth0SessionMarkedHere(): boolean {
    return sessionMarkedHere;
}

/** Takes the marker as a fresh page load would find it. */
export function resetAuth0SessionMarkedHereForTests(): void {
    sessionMarkedHere = readSessionMarker();
}

export function markAuth0TenantSession(): void {
    window.localStorage.setItem(TENANT_SESSION_KEY, 'true');
}

export function hasAuth0TenantSession(): boolean {
    return window.localStorage.getItem(TENANT_SESSION_KEY) === 'true';
}

export function clearAuth0TenantSession(): void {
    window.localStorage.removeItem(TENANT_SESSION_KEY);
}

/** mayHaveAuth0Session without the tenant marker: whether there may be an app session to end. */
export function mayHaveAuth0AppSession(): boolean {
    return isAuth0Configured() && (getAuth0State().isAuthenticated || hasAuth0Session());
}

/**
 * Whether signing out has an Auth0 session to end. Read before anything is cleared:
 * clearing is what removes the evidence.
 */
export function mayHaveAuth0Session(): boolean {
    return mayHaveAuth0AppSession() || (isAuth0Configured() && hasAuth0TenantSession());
}
